import { CLASS_NAMES, EVENTS } from '@bs/constants';
import { MessageBus } from '@bs/utils';
/**
 * A class representing a Snapshot component.
 * @class Snapshot
 */
export default class Snapshot extends MessageBus {
  /**
   * Initializes the component.
   * @param {HTMLElement} element - The element that the component is associated with.
   * @returns {Snapshot} The component instance.
   */
  constructor(element) {
    super();
    this.element = element;
    this.track = null;
    this.cards = [];
    this.btnLeft = null;
    this.btnRight = null;
    this.currentIndex = 0;
    this.startX = 0;

    this.init();
  }

  /**
   * Initializes the component.
   * @returns {Snapshot} The component instance.
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();

    window.addEventListener(EVENTS.LOAD, this.updateCarousel.bind(this));
    window.addEventListener(EVENTS.RESIZE, this.updateCarousel.bind(this));
    return this;
  }

  /**
   * Caches DOM references.
   * @returns {Snapshot} The component instance.
   */
  cacheDomReferences() {
    this.track = this.element.querySelector(
      `.${CLASS_NAMES.SNAPSHOT_CAROUSEL_TRACK}`
    );
    this.cards = Array.from(this.track?.children || []);
    this.btnLeft = this.element.querySelector(`.${CLASS_NAMES.NAV_LEFT}`);
    this.btnRight = this.element.querySelector(`.${CLASS_NAMES.NAV_RIGHT}`);
    return this;
  }

  /**
   * Sets up event handlers.
   * @returns {Snapshot} The component instance.
   */
  setupHandlers() {
    this.onLeftClick = this.onLeftClick.bind(this);
    this.onRightClick = this.onRightClick.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);
    this.updateCarousel = this.updateCarousel.bind(this);
    return this;
  }

  /**
   * Enables the component.
   * @returns {Snapshot} The component instance.
   */
  enable() {
    if (!this.track || this.cards.length === 0) return this;

    this.btnLeft?.addEventListener(EVENTS.CLICK, this.onLeftClick);
    this.btnRight?.addEventListener(EVENTS.CLICK, this.onRightClick);
    this.track.addEventListener(EVENTS.TOUCH_START, this.onTouchStart);
    this.track.addEventListener(EVENTS.TOUCH_END, this.onTouchEnd);

    return this;
  }

  /**
   * Gets the width of a single card.
   * @returns {number} The width of a single card.
   */
  getCardWidth() {
    const card = this.cards[0];
    if (!card) return 0;

    const trackStyle = window.getComputedStyle(this.track);
    const gap = parseFloat(trackStyle.columnGap || trackStyle.gap || '0');
    return card.offsetWidth + gap;
  }

  /**
   * Gets the number of visible cards.
   * @returns {number} The number of visible cards.
   */
  getVisibleCards() {
    const container = this.element.querySelector(
      `.${CLASS_NAMES.SNAPSHOT_CAROUSEL}`
    );
    const cardWidth = this.getCardWidth();
    if (!container || !cardWidth) return 1;

    return Math.floor(container.offsetWidth / cardWidth) || 1;
  }

  /**
   * Updates the carousel.
   */
  updateCarousel() {
    if (!this.track || this.cards.length === 0) return;

    const container = this.element.querySelector(
      `.${CLASS_NAMES.SNAPSHOT_CAROUSEL}`
    );
    const cardWidth = this.getCardWidth();
    const totalCards = this.cards.length;

    const totalTrackWidth = cardWidth * totalCards;
    const containerWidth = container.offsetWidth;

    const maxScroll = Math.max(0, totalTrackWidth - containerWidth);

    let translateX = this.currentIndex * cardWidth;
    translateX = Math.min(translateX, maxScroll);

    // Apply transform
    this.track.style.transform = `translateX(-${translateX}px)`;

    // Update nav button states
    const atStart = this.currentIndex === 0;
    const atEnd = translateX >= maxScroll;

    this.btnLeft.setAttribute('aria-disabled', String(atStart));
    this.btnLeft.tabIndex = atStart ? -1 : 0;

    this.btnRight.setAttribute('aria-disabled', String(atEnd));
    this.btnRight.tabIndex = atEnd ? -1 : 0;

    // Accessibility: update visibility/focusability of cards
    this.cards.forEach((card, index) => {
      const cardStart = index * cardWidth;
      const cardEnd = cardStart + cardWidth;
      const isVisible =
        cardEnd > translateX && cardStart < translateX + containerWidth;

      card.setAttribute('aria-hidden', String(!isVisible));
      Snapshot.setFocusableChildren(card, isVisible); // assuming static method
    });
  }

  /**
   * Enables or disables focus on all focusable elements inside a card.
   * @param {HTMLElement} card - The card element.
   * @param {boolean} enable - Whether to make elements focusable.
   */
  static setFocusableChildren(card, enable) {
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]'
    ];

    const focusables = card.querySelectorAll(focusableSelectors.join(','));

    focusables.forEach((element) => {
      const el = element; // avoid param reassign

      if (enable) {
        // Restore tabindex if it was removed
        if (el.dataset.originalTabindex) {
          el.setAttribute('tabindex', el.dataset.originalTabindex);
          delete el.dataset.originalTabindex;
        } else {
          el.removeAttribute('tabindex');
        }
      } else {
        if (el.hasAttribute('tabindex')) {
          el.dataset.originalTabindex = el.getAttribute('tabindex');
        }
        el.setAttribute('tabindex', '-1');
      }
    });
  }

  /**
   * Handles left button click.
   */
  onLeftClick() {
    if (this.currentIndex > 0) {
      this.currentIndex -= 1;
      this.updateCarousel();
    }
  }

  /**
   * Handles right button click.
   */
  onRightClick() {
    const visibleCount = this.getVisibleCards();
    const maxIndex = Math.max(0, this.cards.length - visibleCount);
    if (this.currentIndex < maxIndex) {
      this.currentIndex += 1;
      this.updateCarousel();
    }
  }

  /**
   * Handles touch start event.
   * @param {TouchEvent} e - The touch event.
   */
  onTouchStart(e) {
    this.startX = e.touches[0].clientX;
  }

  /**
   * Handles touch end event.
   * @param {TouchEvent} e - The touch event.
   */
  onTouchEnd(e) {
    const endX = e.changedTouches[0].clientX;
    const threshold = 30;

    if (endX < this.startX - threshold) {
      this.onRightClick();
    } else if (endX > this.startX + threshold) {
      this.onLeftClick();
    }
  }
}
